import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";

import { useGroup } from "./GroupContext";
import { useRow } from "./RowContext";
import { useTable } from "./TableContext";

import styles from "./ComparisonTable.scss";

type ComparisonTableColBaseProps = {
  index?: number;
};
export type ComparisonTableColProps = ComparisonTableColBaseProps &
  Omit<React.ComponentPropsWithoutRef<"td">, keyof ComparisonTableColBaseProps>;

const defaultProps = {
  index: 0,
  children: undefined,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  ComparisonTableColBaseProps,
  typeof defaultProps
> = defaultProps;

const buildHeadersValue = (possibleHeaders: string[]): string => {
  const result: string[] = [];

  possibleHeaders.forEach((header) => {
    if (header) {
      result.push(header);
    }
  });

  return result.join(" ");
};

export const ComparisonTableCol: ((
  props: ComparisonTableColProps
) => JSX.Element) &
  ReactComponentStaticProps<ComparisonTableColProps> = ({
  children = defaultProps.children,
  index = defaultProps.index,
  ...restProps
}) => {
  const { headerRowId } = useGroup();
  const { headerColId } = useRow();
  const { hasColumnHeader, headerColumnIds } = useTable();
  const headersValue = buildHeadersValue([
    headerColumnIds[index],
    headerRowId,
    headerColId,
  ]);

  return (
    <td
      className={cx(styles.col, { [styles["col--centered"]]: hasColumnHeader })}
      headers={headersValue}
      {...restProps}
    >
      {children}
    </td>
  );
};

ComparisonTableCol.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  index: PropTypes.number,
};

ComparisonTableCol.defaultProps = validatedDefaultProps;

ComparisonTableCol.displayName = "ComparisonTable.Col";

export default ComparisonTableCol;
